import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { HeaderBar, HeaderIconButton } from '../components/HeaderBar.tsx';
import { Icon } from '../components/Icon.tsx';
import { useMe } from '../api/hooks.ts';
import {
  useCreateMeasurement,
  useDeleteMeasurement,
  useMeasurements,
  useUpdateMeasurement,
} from '../api/measurements.ts';
import { GrowthCard } from '../components/growth/GrowthCard.tsx';
import { GrowthChart } from '../components/growth/GrowthChart.tsx';
import { TargetHeightCard } from '../components/growth/TargetHeightCard.tsx';
import { AdviceAlertCard } from '../components/growth/AdviceAlertCard.tsx';
import { GrowthAboutSheet } from '../components/growth/GrowthAboutSheet.tsx';
import { GrowthChartFullSheet } from '../components/growth/GrowthChartFullSheet.tsx';
import { growth } from '@shared/index.ts';
import { TimelineEntry, TimelineSpine, type SpineItem } from '../components/growth/TimelineSpine.tsx';
import { MeasurementSheet } from '../components/sheets/MeasurementSheet.tsx';
import { KIND_META, fmtMeasurement, fmtAge, ageDaysAt } from '../lib/measure.ts';
import { FEVER_THRESHOLD_C, MEASUREMENT_KINDS, type MeasurementKind } from '@shared/constants.ts';

// /growth only handles "actual growth" measurements. Temperature lives in
// its own flow (see TXX) so it doesn't muddle the percentile-curve UI.
const GROWTH_KINDS = MEASUREMENT_KINDS.filter((k) => k !== 'temperature') as readonly Exclude<
  MeasurementKind,
  'temperature'
>[];
import type { Measurement } from '@shared/types.ts';
import { relAgo } from '../lib/format.ts';

export function Growth() {
  const { data: me } = useMe();
  const baby = me?.babies.find((b) => b.id === me?.active_baby_id) ?? me?.babies[0] ?? null;
  const { data: measurements = [] } = useMeasurements(baby?.id);

  const [addingKind, setAddingKind] = useState<MeasurementKind | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [chartFullOpen, setChartFullOpen] = useState(false);
  const [editing, setEditing] = useState<Measurement | null>(null);
  const [activeTab, setActiveTab] = useState<MeasurementKind>('weight');

  // Open the add sheet on arrival when navigated with ?add=1 (from the
  // Home "Log anything" picker). Clear the param after consuming so a
  // refresh doesn't re-open.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('add') === '1') {
      setAddingKind('weight');
      const next = new URLSearchParams(params);
      next.delete('add');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = useCreateMeasurement();
  const update = useUpdateMeasurement();
  const del = useDeleteMeasurement();

  const showCurves = !!me?.settings?.growth_show_curves;
  const reference = (me?.settings?.growth_reference ?? 'nl5') as 'nl4' | 'nl5' | 'who';
  const showTH = !!me?.settings?.growth_show_target_height;

  const byKind = useMemo(() => {
    const out: Record<MeasurementKind, Measurement[]> = {
      weight: [],
      length: [],
      head_circumference: [],
      temperature: [],
    };
    for (const m of measurements) out[m.kind].push(m);
    for (const k of MEASUREMENT_KINDS) out[k].sort((a, b) => b.occurred_at - a.occurred_at);
    // Don't surface temperatures on the growth timeline — they belong on /health.
    out.temperature = [];
    return out;
  }, [measurements]);

  // Per-metric SDS trails. Only meaningful with sex set; reference + ga
  // route the table inside the advice tree.
  const sdsTrails = useMemo<Partial<Record<MeasurementKind, { sds: number | null }[]>>>(() => {
    if (!baby?.sex) return {};
    const birthSec = isoToUtcSec(baby.birth_date);
    const w = byKind.weight.length
      ? growth.calculateWeightAdvice({
          sex: baby.sex,
          birthSec,
          gaDays: baby.ga_days,
          reference,
          measurements: byKind.weight.map((m) => ({ occurredAt: m.occurred_at, weightKg: m.value / 1000 })),
        }).sdsTrail
      : null;
    const l = byKind.length.length
      ? growth.calculateHgtAdvice({
          sex: baby.sex,
          birthSec,
          reference,
          measurements: byKind.length.map((m) => ({ occurredAt: m.occurred_at, lengthCm: m.value / 10 })),
        }).sdsTrail
      : null;
    const h = byKind.head_circumference.length
      ? growth.calculateHdcAdvice({
          sex: baby.sex,
          birthSec,
          reference,
          measurements: byKind.head_circumference.map((m) => ({ occurredAt: m.occurred_at, hcCm: m.value / 10 })),
        }).sdsTrail
      : null;
    return {
      weight: w ?? undefined,
      length: l ?? undefined,
      head_circumference: h ?? undefined,
    };
  }, [baby?.sex, baby?.birth_date, baby?.ga_days, byKind, reference]);

  // Birth-z context lines (AGA/SGA/LGA) — only when sex + ga + the
  // relevant first measurement are all present, and the measurement was
  // taken within the first 3 days (so it actually represents birth).
  const { birthZHint, birthLengthHint } = useMemo(() => {
    if (!baby?.sex || baby.ga_days == null) return { birthZHint: null, birthLengthHint: null };
    const gaWeeks = Math.floor(baby.ga_days / 7);
    const birthSec = isoToUtcSec(baby.birth_date);
    const oldestWeight = byKind.weight[byKind.weight.length - 1];
    const oldestLength = byKind.length[byKind.length.length - 1];
    const isAtBirth = (occurredAt: number) => Math.abs(occurredAt - birthSec) <= 3 * 86_400;

    let wHint: string | null = null;
    if (oldestWeight && isAtBirth(oldestWeight.occurred_at)) {
      const z = growth.birthWeightZ(oldestWeight.value, { sex: baby.sex, gaWeeks });
      if (z != null) {
        const cls = growth.classifyBirthZ(z);
        wHint = `Birth · ${cls} · z ${z >= 0 ? '+' : ''}${z.toFixed(2)}`;
      }
    }
    let lHint: string | null = null;
    if (oldestLength && isAtBirth(oldestLength.occurred_at)) {
      const z = growth.birthLengthZ(oldestLength.value / 10, { sex: baby.sex, gaWeeks });
      if (z != null) {
        const cls = growth.classifyBirthZ(z);
        lHint = `Birth · ${cls} · z ${z >= 0 ? '+' : ''}${z.toFixed(2)}`;
      }
    }
    return { birthZHint: wHint, birthLengthHint: lHint };
  }, [baby?.sex, baby?.birth_date, baby?.ga_days, byKind.weight, byKind.length]);

  const spineItems = useMemo<SpineItem[]>(
    () =>
      measurements.map((m) => ({
        key: m.id,
        at: m.occurred_at,
        accent: `var(--${KIND_META[m.kind].accent})`,
        render: () => (
          <MeasurementRow m={m} birthDate={baby?.birth_date ?? null} onClick={() => setEditing(m)} />
        ),
      })),
    [measurements, baby?.birth_date],
  );

  return (
    <>
      <HeaderBar
        baby={baby}
        title="Growth"
        subtitle={baby ? `${baby.name} · ${fmtAge(ageDaysAt(baby.birth_date, Math.floor(Date.now() / 1000)))}` : null}
        rightSlot={
          <div style={{ display: 'flex', gap: 6 }}>
            <HeaderIconButton ariaLabel="About growth metrics" onClick={() => setAboutOpen(true)}>
              <Icon.info s={18} />
            </HeaderIconButton>
            <HeaderIconButton ariaLabel="Add measurement" onClick={() => setAddingKind('weight')}>
              <Icon.plus s={18} />
            </HeaderIconButton>
          </div>
        }
      />
      <GrowthAboutSheet open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <div className="lt-scroll" style={{ flex: 1, padding: '0 16px 24px' }}>
        {showCurves && baby && !baby.sex && (
          <div
            style={{
              marginBottom: 12,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--warn-soft)',
              border: '1px solid color-mix(in oklab, var(--warn) 30%, transparent)',
              fontSize: 13,
              color: 'var(--text-2)',
            }}
          >
            Set {baby.name}'s sex in <strong>More → Babies & Family</strong> to render growth curves.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {GROWTH_KINDS.map((k) => (
            <GrowthCard
              key={k}
              kind={k}
              measurements={byKind[k]}
              onAdd={() => setAddingKind(k)}
              sdsTrail={sdsTrails[k] ?? undefined}
              contextHint={k === 'weight' ? birthZHint : k === 'length' ? birthLengthHint : null}
            />
          ))}
        </div>

        {baby && (
          <div
            style={{
              marginTop: 18,
              padding: 16,
              borderRadius: 22,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--bg-sunken)', borderRadius: 999, marginBottom: 12 }}>
              {GROWTH_KINDS.map((k) => {
                const on = k === activeTab;
                return (
                  <button
                    type="button"
                    key={k}
                    onClick={() => setActiveTab(k)}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      background: on ? 'var(--bg-elevated)' : 'transparent',
                      color: on ? 'var(--text-1)' : 'var(--text-3)',
                      boxShadow: on ? 'var(--shadow-sm)' : 'none',
                    }}
                  >
                    {KIND_META[k].label}
                  </button>
                );
              })}
            </div>
            <GrowthChart
              kind={activeTab}
              birthDate={baby.birth_date}
              measurements={byKind[activeTab]}
              showCurves={showCurves}
              reference={reference}
              sex={baby.sex}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
              <button
                type="button"
                onClick={() => setChartFullOpen(true)}
                style={{
                  background: 'transparent',
                  color: 'var(--text-3)',
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '4px 2px',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  textDecorationColor: 'var(--divider)',
                }}
              >
                Full view
              </button>
            </div>
          </div>
        )}

        {baby && (
          <GrowthChartFullSheet
            open={chartFullOpen}
            onClose={() => setChartFullOpen(false)}
            kind={activeTab}
            birthDate={baby.birth_date}
            measurements={byKind[activeTab]}
            showCurves={showCurves}
            reference={reference}
            sex={baby.sex}
            currentSds={lastValidSds(sdsTrails[activeTab])}
          />
        )}

        {baby && showTH && (
          <div style={{ marginTop: 14 }}>
            <TargetHeightCard baby={baby} members={me?.members ?? []} />
          </div>
        )}

        {baby && reference === 'nl5' && baby.sex && (
          <AdviceStack
            sex={baby.sex}
            birthDate={baby.birth_date}
            gaDays={baby.ga_days}
            weight={byKind.weight}
            length={byKind.length}
            hc={byKind.head_circumference}
          />
        )}

        {measurements.length > 0 ? (
          <div style={{ marginTop: 22 }}>
            <div
              style={{
                padding: '0 4px 10px',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.04,
              }}
            >
              Timeline
            </div>
            <TimelineSpine items={spineItems} scale="days" />
          </div>
        ) : (
          <div
            style={{
              marginTop: 22,
              padding: '32px 16px',
              borderRadius: 18,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              textAlign: 'center',
              color: 'var(--text-3)',
              fontSize: 13,
            }}
          >
            Tap + to log {baby?.name ?? 'this baby'}'s first measurement.
          </div>
        )}
      </div>

      <MeasurementSheet
        open={!!addingKind}
        defaultKind={addingKind ?? 'weight'}
        kinds={GROWTH_KINDS}
        onClose={() => setAddingKind(null)}
        onSubmit={async (vars) => {
          try {
            await create.mutateAsync(vars);
            toast.success(`${KIND_META[vars.kind].label} saved`);
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed');
          }
        }}
      />
      <MeasurementSheet
        open={!!editing}
        existing={editing}
        defaultKind={editing?.kind ?? 'weight'}
        kinds={GROWTH_KINDS}
        onClose={() => setEditing(null)}
        onSubmit={async (vars) => {
          if (!editing) return;
          try {
            await update.mutateAsync({
              id: editing.id,
              patch: {
                kind: vars.kind,
                value: vars.value,
                occurred_at: vars.occurred_at,
                notes: vars.notes,
              },
            });
            toast.success('Saved');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Save failed');
          }
        }}
        onDelete={async () => {
          if (!editing) return;
          try {
            await del.mutateAsync(editing.id);
            toast.success('Deleted');
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Delete failed');
          }
        }}
      />
    </>
  );
}

// Runs all three advice trees and emits an AdviceAlertCard per metric
// whose code is non-normal. Heuristic: 2074/2075/3041-3045/1044/1045/1047/
// 1048/1077 are alerts; everything else stays silent.
const ALERT_CODES = new Set([2074, 2075, 3041, 3042, 3043, 3044, 3045, 1044, 1045, 1048, 1077]);
const DANGER_CODES = new Set([2074, 2075, 3041, 3042, 3043, 1044, 1045, 1048]);

function isoToUtcSec(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.floor(Date.UTC(y, (m ?? 1) - 1, d ?? 1) / 1000);
}

function lastValidSds(trail: { sds: number | null }[] | undefined): number | null {
  if (!trail) return null;
  for (let i = trail.length - 1; i >= 0; i--) {
    if (trail[i].sds != null) return trail[i].sds;
  }
  return null;
}

function AdviceStack({
  sex,
  birthDate,
  gaDays,
  weight,
  length,
  hc,
}: {
  sex: 'M' | 'F';
  birthDate: string;
  gaDays: number | null;
  weight: Measurement[];
  length: Measurement[];
  hc: Measurement[];
}) {
  const birthSec = isoToUtcSec(birthDate);
  const wAdv = weight.length
    ? growth.calculateWeightAdvice({
        sex,
        birthSec,
        gaDays,
        measurements: weight.map((m) => ({ occurredAt: m.occurred_at, weightKg: m.value / 1000 })),
      })
    : null;
  const lAdv = length.length
    ? growth.calculateHgtAdvice({
        sex,
        birthSec,
        measurements: length.map((m) => ({ occurredAt: m.occurred_at, lengthCm: m.value / 10 })),
      })
    : null;
  const hAdv = hc.length
    ? growth.calculateHdcAdvice({
        sex,
        birthSec,
        measurements: hc.map((m) => ({ occurredAt: m.occurred_at, hcCm: m.value / 10 })),
      })
    : null;

  const cards: { metric: string; tone: 'danger' | 'warn'; result: growth.AdviceResult }[] = [];
  for (const [metric, adv] of [['Weight', wAdv], ['Length', lAdv], ['Head circumference', hAdv]] as const) {
    if (!adv || !ALERT_CODES.has(adv.msgcode)) continue;
    cards.push({ metric, tone: DANGER_CODES.has(adv.msgcode) ? 'danger' : 'warn', result: adv });
  }
  if (!cards.length) return null;
  return (
    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {cards.map((c) => (
        <AdviceAlertCard key={c.metric} metric={c.metric} tone={c.tone} result={c.result} />
      ))}
    </div>
  );
}

function MeasurementRow({
  m,
  birthDate,
  onClick,
}: {
  m: Measurement;
  birthDate: string | null;
  onClick: () => void;
}) {
  const meta = KIND_META[m.kind];
  const age = birthDate ? fmtAge(ageDaysAt(birthDate, m.occurred_at)) : null;
  const d = new Date(m.occurred_at * 1000);
  const isFever = m.kind === 'temperature' && m.value >= FEVER_THRESHOLD_C * 10;
  return (
    <TimelineEntry
      onClick={onClick}
      hasNotes={!!m.notes}
      title={
        <span className="lt-tabular" style={{ display: 'inline-flex', gap: 6 }}>
          <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>{meta.label}</span>
          <span style={isFever ? { color: 'var(--danger)' } : undefined}>
            {fmtMeasurement(m.kind, m.value)}
          </span>
          {isFever && (
            <span style={{ color: 'var(--danger)', fontSize: 11, fontWeight: 600 }}>fever</span>
          )}
        </span>
      }
      subtitle={
        <>
          {age && `${age} · `}
          {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} ·{' '}
          {relAgo(m.occurred_at)}
        </>
      }
    />
  );
}
